import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData, updateData } from '../../../apiService';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
    Box, Typography, CircularProgress, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, IconButton, Grid, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import { Build, SwapHoriz, LocalShipping, ChevronRight, Close, Person, LocationOn, Receipt, CheckCircle, BadgeOutlined } from '@mui/icons-material';
import { TextField } from '@mui/material';

const STATUS_OPTIONS = [
    { value: 'Scheduled', label: 'Scheduled', color: '#2563eb', bg: '#eff6ff' },
    { value: 'Hold', label: 'Hold', color: '#d97706', bg: '#fffbeb' },
    { value: 'Completed', label: 'Completed', color: '#16a34a', bg: '#f0fdf4' },
];

/* ── Design Tokens ── */
const C = {
    bg: '#f1f5f9',
    card: '#ffffff',
    border: '#e2e8f0',
    text: '#0f172a',
    sec: '#475569',
    muted: '#94a3b8',
    blue: '#1e40af',
    purple: '#7c3aed',
    orange: '#ea580c',
    green: '#16a34a',
    red: '#dc2626',
};

const fmtDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
};
const fmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/* ── Status Chip ── */
const StatusChip = ({ label, color }) => (
    <Box sx={{ px: 0.9, py: 0.2, borderRadius: 10, bgcolor: `${color}18`, border: `1px solid ${color}30`, display: 'inline-flex' }}>
        <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color, lineHeight: 1.5 }}>{label}</Typography>
    </Box>
);

/* ── Info Row inside modal ── */
const InfoItem = ({ label, value, color }) => (
    <Box sx={{ mb: 0.8 }}>
        <Typography sx={{ fontSize: '0.62rem', color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: color || C.text, mt: 0.1 }}>{value || '-'}</Typography>
    </Box>
);

/* ── Section heading inside modal ── */
const SectionHead = ({ icon, label, color }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, mt: 1.5 }}>
        <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon}
        </Box>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</Typography>
    </Box>
);

/* ── Row inside card ── */
const DataRow = ({ left, right, sub, color, last, onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            py: 0.85,
            borderBottom: last ? 'none' : `1px solid ${C.border}`,
            cursor: onClick ? 'pointer' : 'default',
            borderRadius: 1,
            px: 0.5,
            '&:hover': onClick ? { bgcolor: `${color}08` } : {},
            transition: 'background 0.15s',
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: C.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{left}</Typography>
            {right && <StatusChip label={right} color={color} />}
        </Box>
        {sub && <Typography sx={{ fontSize: '0.65rem', color: C.muted, mt: 0.2 }}>{sub}</Typography>}
    </Box>
);

/* ════════════════════════════════════════
   DETAIL MODAL
════════════════════════════════════════ */
function DetailModal({ open, row, onClose, onAllocate, allocating, companyId, token }) {
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedSalesman, setSelectedSalesman] = useState('');
    const [salesmanList, setSalesmanList] = useState([]);
    const [salesmanLoading, setSalesmanLoading] = useState(false);

    const [originalSalesman, setOriginalSalesman] = useState('');
    const [originalStatus, setOriginalStatus] = useState('');
    const [installationDate, setInstallationDate] = useState("");
    const [originalInstallationDate, setOriginalInstallationDate] = useState("");


    useEffect(() => {
        if (open) {
            const initStatus = row?.InstallationStatus || row?.Status || '';
            setSelectedStatus(initStatus);
            setOriginalStatus(initStatus);
            const initSalesman = row?.MasterId || row?.SalesPersonId || row?.StaffMasterId || row?.StaffId || '';
            setSelectedSalesman(initSalesman);
            setOriginalSalesman(initSalesman);

            setInstallationDate(
                (row?.ScheduleDate || row?.InstallationDate || "").split("T")[0]
            );

            const initDate = (row?.ScheduleDate || row?.InstallationDate || "").split("T")[0];

            setInstallationDate(initDate);
            setOriginalInstallationDate(initDate);
        }
    }, [open, row]);

    /* Fetch salesman list for exchange and cod types */
    useEffect(() => {
        if (open && row && (row._type === 'exchange' || row._type === 'cod') && companyId && token) {
            const fetchStaff = async () => {
                setSalesmanLoading(true);
                try {
                    const response = await fetchData('SalesExecutiveApi', `/${companyId}`, token);
                    const list = response.response_data || response || [];
                    setSalesmanList(list);
                    // Pre-select already assigned salesman after list loads
                    const existingId = row?.MasterId || row?.SalesPersonId || row?.StaffMasterId || row?.StaffId || '';
                    if (existingId) setSelectedSalesman(existingId);
                } catch (error) {
                    console.error('Error fetching staff:', error);
                    setSalesmanList([]);
                } finally {
                    setSalesmanLoading(false);
                }
            };
            fetchStaff();
        }
    }, [open, row, companyId, token]);

    if (!row) return null;

    const type = row._type;
    const accentColor = type === 'installation' ? C.blue : type === 'exchange' ? C.purple : C.orange;
    const codAmt = type === 'cod' ? parseFloat(row.ToCollect || row.BalanceAmount || row.CODAmount || 0) : 0;
    const isCollected = type === 'cod' ? (row.IsDelivered || row.IsCODCollected || codAmt === 0) : false;
    const statusLabel = type === 'cod' ? (row.AllocatedDate ? 'Processing' : 'Pending') :
        type === 'exchange' ? (row.AllocatedDate ? 'Processing' : 'Pending') :
            (row.InstallationStatus || row.Status || 'Pending');
    const titleMap = { installation: 'Installation Detail', exchange: 'Exchange Detail', cod: 'COD Detail' };

    const currentStatusOpt = STATUS_OPTIONS.find(s => s.value === selectedStatus);
    // Exchange & COD: show salesman. Installation: show status only.
    const showSalesman = type === 'exchange' || type === 'cod';
    const showStatus = type === 'installation';

    const isStatusChanged = selectedStatus !== originalStatus;
    const isDateChanged = installationDate !== originalInstallationDate;

    const hasChanges = isStatusChanged || isDateChanged;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs"
            PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>

            {/* Title bar */}
            <DialogTitle sx={{ p: 0 }}>
                <Box sx={{ background: `linear-gradient(135deg, ${accentColor}dd, ${accentColor})`, px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {type === 'installation' && <Build sx={{ fontSize: 15, color: '#fff' }} />}
                            {type === 'exchange' && <SwapHoriz sx={{ fontSize: 15, color: '#fff' }} />}
                            {type === 'cod' && <LocalShipping sx={{ fontSize: 15, color: '#fff' }} />}
                        </Box>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>{titleMap[type]}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ px: 1, py: 0.3, borderRadius: 10, bgcolor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)' }}>
                            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#fff' }}>{statusLabel}</Typography>
                        </Box>
                        <IconButton size="small" onClick={onClose} sx={{ color: '#fff', p: 0.3 }}>
                            <Close sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: 2, pt: 1.5, pb: 0 }}>

                {/* ── Sale / Bill Info ── */}
                <SectionHead icon={<Receipt sx={{ fontSize: 13, color: accentColor }} />} label="Sale Details" color={accentColor} />
                <Box sx={{ bgcolor: C.bg, borderRadius: 2, p: 1.5 }}>
                    <Grid container spacing={1.5}>
                        <Grid item xs={6}><InfoItem label="Bill No" value={row.BillNumber || row.BillNo} color={accentColor} /></Grid>
                        <Grid item xs={6}><InfoItem label="Bill Date" value={fmtDate(row.CreatedDate || row.SaleDate)} /></Grid>
                        {type === 'installation' && <>
                            {/* <Grid item xs={6}><InfoItem label="Schedule Date" value={fmtDate(row.ScheduleDate || row.InstallationDate)} /></Grid> */}
                            <Grid item xs={5}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mb: 0.5 }}
                                >
                                    Schedule Date
                                </Typography>

                                <TextField
                                    fullWidth
                                    type="date"
                                    size="small"
                                    value={installationDate}
                                    onChange={(e) => setInstallationDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        mt: 0.3,
                                        '& .MuiInputBase-input': {
                                            py: '6px',
                                            fontSize: '0.8rem'
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={6}><InfoItem label="Product" value={row.ProductName || row.ItemName} /></Grid>
                        </>}
                        {type === 'exchange' && <>
                            <Grid item xs={12}><InfoItem label="Exchange Item" value={row.ExchangeItemName || row.OldItemName} /></Grid>
                            {row.TotalAmount != null && <Grid item xs={6}><InfoItem label="Amount" value={fmt(row.TotalAmount)} color={accentColor} /></Grid>}
                        </>}
                        {type === 'cod' && <>
                            <Grid item xs={6}><InfoItem label="To Collect" value={fmt(codAmt)} color={isCollected ? C.green : C.red} /></Grid>
                            {row.BillTotal != null && <Grid item xs={6}><InfoItem label="Grand Total" value={fmt(row.BillTotal)} /></Grid>}
                        </>}
                    </Grid>
                </Box>

                {/* ── Customer Info ── */}
                <SectionHead icon={<Person sx={{ fontSize: 13, color: accentColor }} />} label="Customer" color={accentColor} />
                <Box sx={{ bgcolor: C.bg, borderRadius: 2, p: 1.5 }}>
                    <Grid container spacing={1.5}>
                        <Grid item xs={12}><InfoItem label="Name" value={row.CustomerName} /></Grid>
                        {/* <Grid item xs={6}><InfoItem label="Contact" value={row.ContactNumber || row.CustomerContactNumber || row.MobileNumber} /></Grid> */}
                        {row.EmailId && <Grid item xs={6}><InfoItem label="Email" value={row.EmailId} /></Grid>}
                    </Grid>
                </Box>

                {/* ── Address ── */}
                {(row.Address || row.CustomerAddress || row.DeliveryAddress) && <>
                    <SectionHead icon={<LocationOn sx={{ fontSize: 13, color: accentColor }} />} label="Address" color={accentColor} />
                    <Box sx={{ bgcolor: C.bg, borderRadius: 2, p: 1.5 }}>
                        <Typography sx={{ fontSize: '0.82rem', color: C.sec, lineHeight: 1.6 }}>
                            {row.Address || row.CustomerAddress || row.DeliveryAddress}
                        </Typography>
                        {(row.City || row.Pincode) && (
                            <Typography sx={{ fontSize: '0.75rem', color: C.muted, mt: 0.3 }}>
                                {[row.City, row.Pincode].filter(Boolean).join(' – ')}
                            </Typography>
                        )}
                    </Box>
                </>}

                {/* ── Salesman Dropdown (Exchange only) ── */}
                {showSalesman && (
                    <>
                        <SectionHead icon={<BadgeOutlined sx={{ fontSize: 13, color: accentColor }} />} label="Assign Salesman" color={accentColor} />
                        <Box sx={{ mb: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ fontSize: '0.78rem' }}>
                                    {salesmanLoading ? 'Loading...' : 'Select Salesman'}
                                </InputLabel>
                                <Select
                                    value={selectedSalesman}
                                    onChange={(e) => setSelectedSalesman(e.target.value)}
                                    label={salesmanLoading ? 'Loading...' : 'Select Salesman'}
                                    disabled={salesmanLoading}
                                    sx={{
                                        borderRadius: '10px', fontSize: '0.82rem',
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: accentColor },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor },
                                    }}
                                >
                                    <MenuItem value="" sx={{ fontSize: '0.78rem', color: C.muted }}>
                                        — None —
                                    </MenuItem>
                                    {salesmanList.map((s) => {
                                        const id = s.MasterId || s.Id;
                                        const name = s.MasterName || s.Name ||
                                            (s.FirstName ? `${s.FirstName} ${s.LastName || ''}`.trim() : s.StaffName || '');
                                        return (
                                            <MenuItem key={id} value={id} sx={{ fontSize: '0.82rem' }}>
                                                {name}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Box>
                    </>
                )}

                {/* ── Status (Installation & COD only) ── */}
                {showStatus && (
                    <>
                        <SectionHead icon={<CheckCircle sx={{ fontSize: 13, color: accentColor }} />} label="Update Status" color={accentColor} />
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            {STATUS_OPTIONS.map((opt) => {
                                const active = selectedStatus === opt.value;
                                return (
                                    <Box key={opt.value} onClick={() => setSelectedStatus(opt.value)}
                                        sx={{
                                            flex: 1, py: 1, borderRadius: '10px', cursor: 'pointer', textAlign: 'center',
                                            border: `2px solid ${active ? opt.color : C.border}`,
                                            bgcolor: active ? opt.bg : '#fff',
                                            transition: 'all 0.15s',
                                            '&:hover': { borderColor: opt.color, bgcolor: opt.bg },
                                        }}>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: active ? opt.color : C.muted }}>
                                            {opt.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5, borderTop: `1px solid ${C.border}`, gap: 1 }}>
                <Button onClick={onClose} size="small" variant="outlined"
                    sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '0.78rem', color: C.sec, borderColor: C.border, px: 2 }}>
                    Cancel
                </Button>
                <Button
                    //onClick={() => onAllocate(row, selectedStatus, selectedSalesman, salesmanList.find(s => (s.MasterId || s.Id) === selectedSalesman))}
                    //disabled={showSalesman ? (!selectedSalesman || allocating || selectedSalesman === originalSalesman) : (!selectedStatus || allocating || selectedStatus === originalStatus)}
                    onClick={() =>
                        onAllocate(
                            row,
                            selectedStatus,
                            selectedSalesman,
                            salesmanList.find(s => (s.MasterId || s.Id) === selectedSalesman),
                            installationDate
                        )
                    }
                    disabled={
                        showSalesman
                            ? (!selectedSalesman || allocating || selectedSalesman === originalSalesman)
                            : (!selectedStatus || allocating || !hasChanges)
                    }
                    size="small" variant="contained"
                    sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '0.78rem', bgcolor: currentStatusOpt?.color || accentColor, px: 2.5, '&:hover': { filter: 'brightness(0.88)', bgcolor: currentStatusOpt?.color || accentColor } }}>
                    {allocating ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Update'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ════════════════════════════════════════
   SCHEDULE CARD (with data)
════════════════════════════════════════ */
const ScheduleCard = ({ icon, title, subtitle, color, gradient, data, loading, onPress, onRowClick }) => {
    const preview = data.slice(0, 4);
    const remaining = data.length - preview.length;

    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', bgcolor: C.card, borderRadius: 3,
            border: `1px solid ${C.border}`, borderTop: `4px solid ${color}`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden', transition: 'all 0.18s ease',
            '&:hover': { boxShadow: `0 6px 20px ${color}25`, transform: 'translateY(-2px)' },
        }}>
            {/* Card Header */}
            <Box sx={{ px: 2, pt: 2, pb: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 3px 8px ${color}40` }}>
                        {icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: C.text, lineHeight: 1.2 }}>{title}</Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: C.muted }}>{subtitle}</Typography>
                    </Box>
                    <Box sx={{ px: 1.1, py: 0.3, borderRadius: 10, bgcolor: `${color}12`, border: `1px solid ${color}25` }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color }}>{data.length}</Typography>
                    </Box>
                </Box>
            </Box>

            <Divider sx={{ borderColor: `${color}20` }} />

            {/* Data Rows */}
            <Box sx={{ px: 2, flex: 1, minHeight: 140 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, gap: 1 }}>
                        <CircularProgress size={18} sx={{ color }} />
                        <Typography sx={{ fontSize: '0.75rem', color: C.muted }}>Loading...</Typography>
                    </Box>
                ) : preview.length === 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: C.muted }}>No records found</Typography>
                    </Box>
                ) : (
                    preview.map((row, i) => {
                        const isLast = i === preview.length - 1 && remaining === 0;
                        if (row._type === 'installation') return (
                            <DataRow key={i}
                                left={row.CustomerName || '-'}
                                right={row.InstallationStatus || row.Status || 'Pending'}
                                sub={`${row.BillNumber || ''} · ${fmtDate(row.ScheduleDate || row.InstallationDate)}`}
                                color={row.InstallationStatus === 'Completed' || row.Status === 'Completed' ? C.green : row.InstallationStatus === 'Inprogress' ? C.blue : '#ca8a04'}
                                last={isLast} onClick={() => onRowClick(row)}
                            />
                        );
                        if (row._type === 'exchange') {
                            const exchStatus = row.AllocatedDate ? 'Processing' : 'Pending';
                            const exchSub = row.AllocatedDate
                                ? `${row.BillNumber || ''} · ${row.ItemName || row.ProductName || ''} · ${fmtDate(row.AllocatedDate)}`
                                : `${row.BillNumber || ''} · ${row.ItemName || row.ProductName || ''}`;
                            return (
                                <DataRow key={i}
                                    left={row.CustomerName || '-'}
                                    right={exchStatus}
                                    sub={exchSub}
                                    color={exchStatus === 'Processing' ? C.blue : C.purple}
                                    last={isLast} onClick={() => onRowClick(row)}
                                />
                            );
                        }
                        if (row._type === 'cod') {
                            const codAmt = parseFloat(row.ToCollect || row.BalanceAmount || row.CODAmount || 0);
                            const codStatus = row.AllocatedDate ? 'Processing' : 'Pending';
                            const codSub = row.AllocatedDate
                                ? `${row.BillNumber || ''} · ${fmt(codAmt)} · ${fmtDate(row.AllocatedDate)}`
                                : `${row.BillNumber || ''} · ${fmt(codAmt)}`;
                            return (
                                <DataRow key={i}
                                    left={row.CustomerName || '-'}
                                    right={codStatus}
                                    sub={codSub}
                                    color={codStatus === 'Processing' ? C.blue : C.red}
                                    last={isLast} onClick={() => onRowClick(row)}
                                />
                            );
                        }
                        return null;
                    })
                )}
                {remaining > 0 && (
                    <Box sx={{ py: 0.75, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.68rem', color, fontWeight: 700 }}>+{remaining} more records</Typography>
                    </Box>
                )}
            </Box>

            {/* Footer */}
            <Box onClick={onPress}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, px: 2, py: 1.1, bgcolor: `${color}08`, borderTop: `1px solid ${color}20`, cursor: 'pointer', '&:hover': { bgcolor: `${color}15` } }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color }}>View All</Typography>
                <ChevronRight sx={{ fontSize: 15, color }} />
            </Box>
        </Box>
    );
};

/* ════════════════════════════════════════
   SCHEDULES PAGE
════════════════════════════════════════ */
export default function Schedules() {
    useSessionValidation();
    const history = useHistory();
    const { companyId, branchId, userId, token } = useAuth();
  const { logAudit } = useAuditLog();

    const [instData, setInstData] = useState([]);
    const [exchData, setExchData] = useState([]);
    const [codData, setCodData] = useState([]);
    const [instLoading, setInstLoading] = useState(true);
    const [exchLoading, setExchLoading] = useState(true);
    const [codLoading, setCodLoading] = useState(true);

    /* Modal state */
    const [modalRow, setModalRow] = useState(null);
    const [allocating, setAllocating] = useState(false);
    const [snackMsg, setSnackMsg] = useState('');

    const loadAll = useCallback(async () => {
        if (!companyId || !token) return;

        fetchData('ExecutivedashboardApi', `/InstallationScheduleList/${companyId}/${branchId}`, token)
            .then(res => { const list = res?.response_data || res || []; setInstData((Array.isArray(list) ? list : []).map(r => ({ ...r, _type: 'installation' }))); })
            .catch(() => { }).finally(() => setInstLoading(false));

        fetchData('ExecutivedashboardApi', `/ExchangOnSiteList/${companyId}/${branchId}`, token)
            .then(res => { const list = res?.response_data || res || []; setExchData((Array.isArray(list) ? list : []).map(r => ({ ...r, _type: 'exchange' }))); })
            .catch(() => { }).finally(() => setExchLoading(false));

        fetchData('ExecutivedashboardApi', `/CashOnDeliverySaleList/${companyId}/${branchId}`, token)
            .then(res => { const list = res?.response_data || res || []; setCodData((Array.isArray(list) ? list : []).map(r => ({ ...r, _type: 'cod' }))); })
            .catch(() => { }).finally(() => setCodLoading(false));
    }, [companyId, branchId, token]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const handleRowClick = (row) => setModalRow(row);
    const handleCloseModal = () => { setModalRow(null); setAllocating(false); };

    //const handleAllocate = async (row, status, salesmanId, salesmanObj) => {
        const handleAllocate = async (row,status,salesmanId,salesmanObj,installationDate) => {
        setAllocating(true);
        try {
            const { _type, ...rowData } = row;
            let payload;
            if (_type === 'exchange' || _type === 'cod') {
                // Exchange & COD: assign salesman
                const salesmanMasterId = salesmanObj?.MasterId || salesmanObj?.Id || salesmanId;
                const salesmanName = salesmanObj?.MasterName || salesmanObj?.Name ||
                    (salesmanObj?.FirstName ? `${salesmanObj.FirstName} ${salesmanObj.LastName || ''}`.trim() : '');
                payload = {
                    ...rowData,
                    MasterId: salesmanMasterId,
                    MasterName: salesmanName,
                    AllocatedBy: userId,
                };
                await updateData(_type === 'exchange' ? 'UOMApi' : 'MasterTypeApi', row.Id, payload, token);
            } else {
                // Installation: update status
                if (!status) { setAllocating(false); return; }
                payload = {
                    ...rowData,
                    InstallationDate: installationDate,
                    InstallationStatus: status,
                    ModifiedBy: userId,
                };
                await updateData('ExecutivedashboardApi', row.Id, payload, token);
            }
            setSnackMsg(_type === 'installation' ? `Status updated to "${status}" successfully` : 'Salesman assigned successfully');
            handleCloseModal();
            loadAll();
        } catch {
            setSnackMsg('Update failed. Please try again.');
        } finally {
            setAllocating(false);
        }
    };

    const CARDS = [
        { icon: <Build sx={{ fontSize: 20, color: '#fff' }} />, title: 'Installation', subtitle: 'Installation schedules', color: C.blue, gradient: 'linear-gradient(135deg,#0f2557,#1d4ed8)', path: '/InstallationScheduleList', data: instData, loading: instLoading },
        { icon: <SwapHoriz sx={{ fontSize: 20, color: '#fff' }} />, title: 'Onsite Exchange', subtitle: 'Exchange item list', color: C.purple, gradient: 'linear-gradient(135deg,#3b0764,#7c3aed)', path: '/OnsiteExchangeList', data: exchData, loading: exchLoading },
        { icon: <LocalShipping sx={{ fontSize: 20, color: '#fff' }} />, title: 'Cash On Delivery', subtitle: 'COD sale list', color: C.orange, gradient: 'linear-gradient(135deg,#431407,#ea580c)', path: '/CashOnDeliveryList', data: codData, loading: codLoading },
    ];

    return (
        <Box sx={{ bgcolor: C.bg, minHeight: '100%', pb: 5, p: '10px' }}>

            <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
                    <h4 style={{ fontSize: '17px', fontFamily: 'Times New Roman', fontWeight: '700', margin: 0, textTransform: 'uppercase', color: 'black' }}>Schedule</h4>
                </div>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, alignItems: 'start' }}>
                    {CARDS.map((card) => (
                        <ScheduleCard
                            key={card.path}
                            icon={card.icon} title={card.title} subtitle={card.subtitle}
                            color={card.color} gradient={card.gradient}
                            data={card.data} loading={card.loading}
                            onPress={() => history.push(card.path)}
                            onRowClick={handleRowClick}
                        />
                    ))}
                </Box>
            </div>

            {/* ── Detail / Allocate Modal ── */}
            <DetailModal
                open={!!modalRow}
                row={modalRow}
                onClose={handleCloseModal}
                onAllocate={handleAllocate}
                allocating={allocating}
                companyId={companyId}
                token={token}
            />

            {/* Simple snack */}
            {snackMsg && (
                <Box sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', bgcolor: '#1e293b', color: '#fff', px: 3, py: 1.2, borderRadius: 2, fontSize: '0.82rem', fontWeight: 600, zIndex: 9999, boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}
                    onClick={() => setSnackMsg('')}>
                    {snackMsg}
                </Box>
            )}
        </Box>
    );
}
